import React, { FC, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Loader from "@components/Loader/Loader";
import { RootState } from "@store/store";
import styled from "@emotion/styled";
import { Avatar, Box, Button, CircularProgress, Grid2 as Grid, Paper, Stack } from "@mui/material";
import StyledDrawer from "@components/StyledDrawer";
import { Session } from "@models/session";
import { usePaginatedMessages } from "@hooks/usePaginatedMessages";
import { DateUtil } from "@utils/date.util";
import { Message } from "@models/message";

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 16px;
`;

const Title = styled.div`
  font-size: 32px;
  line-height: 48px;
  font-weight: 400;
  color: #171a1fff;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  flex: 1;
  min-height: 0;
`;

const Label = styled.div`
  font-size: 16px;
  line-height: 26px;
  font-weight: 400;
  color: #6f7787ff;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Value = styled.div`
  font-size: 16px;
  line-height: 26px;
  font-weight: 400;
  color: #171a1fff;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MessagePaper = styled(Paper)(() => ({
  padding: "10px",
  backgroundColor: "#f6f7f8",
}));

const ChatAvatar: FC<{ url?: string }> = ({ url }) => {
  return <Avatar sx={{ height: "30px", width: "30px" }} src={url} />;
};

const AiMessage: FC<{ message: Message }> = ({ message }) => {
  return (
    <Grid container spacing={1}>
      <Grid size={1}>
        <ChatAvatar url={`${process.env.PUBLIC_URL}/images/logo.png`} />
      </Grid>
      <Grid size={9}>
        <MessagePaper>{message.content}</MessagePaper>
      </Grid>
      <Grid size={2}></Grid>
    </Grid>
  );
};

const UserMessage: FC<{ message: Message; avatarUrl?: string }> = ({
  message,
  avatarUrl,
}) => {
  return (
    <Grid container spacing={1} key={message.id}>
      <Grid size={2}></Grid>
      <Grid size={9}>
        <MessagePaper>{message.content}</MessagePaper>
      </Grid>
      <Grid size={1}>
        <ChatAvatar url={avatarUrl} />
      </Grid>
    </Grid>
  );
};

const Messages: FC<{ messages: Message[] }> = ({ messages }) => {
  const selectedPatient = useSelector(
    (state: RootState) => state.home.selectedPatient,
  );

  return (
    <Stack spacing={2}>
      {messages.map((message) => {
        return message.ai ? (
          <AiMessage key={message.id} message={message} />
        ) : (
          <UserMessage
            key={message.id}
            message={message}
            avatarUrl={selectedPatient?.avatarUrl}
          />
        );
      })}
    </Stack>
  );
};

const SessionDrawer: FC<{
  onClose: () => void;
  session: Session | undefined;
  onDelete?: () => void;
}> = ({ onClose, session, onDelete }) => {
  const { t } = useTranslation();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const {
    messages,
    isInitialLoading,
    isFetchingMore,
    hasMore,
    loadNextPage,
  } = usePaginatedMessages(selectedPatientId ?? undefined, session?.id);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadNextPageRef = useRef(loadNextPage);
  loadNextPageRef.current = loadNextPage;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          loadNextPageRef.current();
        }
      },
      // Use viewport as root so we react to the drawer's internal scroll.
      { root: null, rootMargin: "100px", threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, messages.length, session?.id]);

  return (
    <StyledDrawer onClose={onClose} open={!!session} width="600px">
      <DrawerContent>
        <Header>
          <Title>{t(`sessions.types.${session?.activityType}`)}</Title>

          {onDelete && (
            <Button variant="outlined" color="error" onClick={onDelete}>
              {t("sessions.drawer.deleteButton")}
            </Button>
          )}
        </Header>

        <Body>
          {!selectedPatientId || isInitialLoading || !session ? (
            <Loader />
          ) : (
            <>
              <Grid container spacing={1}>
                <Grid size={4}>
                  <Label>{t("sessions.drawer.date")}</Label>
                </Grid>
                <Grid size={8}>
                  <Value>{DateUtil.formatDateTime(session.createdDate)}</Value>
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid size={4}>
                  <Label>{t("sessions.drawer.duration")}</Label>
                </Grid>
                <Grid size={8}>
                  <Value>
                    {session.endTime
                      ? DateUtil.formatDuration(
                          Date.parse(session.endTime) -
                            Date.parse(session.createdDate),
                        )
                      : t("sessions.inProgress")}
                  </Value>
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid size={4}>
                  <Label>{t("sessions.drawer.summary")}</Label>
                </Grid>
                <Grid size={8}>
                  <Value>{session.summary}</Value>
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Label>{t("sessions.drawer.messages")}</Label>
              </Grid>
              {!messages?.length ? (
                t("sessions.drawer.noMessages")
              ) : (
                <>
                  <Messages messages={messages} />
                  {hasMore && (
                    <Box
                      ref={sentinelRef}
                      sx={{
                        minHeight: 24,
                        display: "flex",
                        justifyContent: "center",
                        py: 1,
                      }}
                    >
                      {isFetchingMore && <CircularProgress size={20} />}
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Body>
      </DrawerContent>
    </StyledDrawer>
  );
};

export default SessionDrawer;
